import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';

interface SidebarContextType {
  isSidebarCollapsed: boolean;
  isMobileSidebarOpen: boolean;
  toggleSidebar: () => void;
  toggleMobileSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  openGroups: Record<string, boolean>;
  setGroupOpen: (key: string, open: boolean) => void;
}

const OPEN_GROUPS_STORAGE_KEY = 'sidebarOpenGroups';

const readOpenGroups = (): Record<string, boolean> => {
  try {
    const saved = localStorage.getItem(OPEN_GROUPS_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider = ({ children }: SidebarProviderProps) => {
  // Inizializza lo stato dal localStorage o usa false come default
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Stato per il mobile sidebar
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Salva lo stato nel localStorage ogni volta che cambia
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Sezioni aperte del menu. Vive qui e non nella Sidebar perché ogni pagina monta il
  // proprio PrivateLayout: navigando la Sidebar si rimonta e perderebbe lo stato,
  // richiudendo le sezioni aperte a mano. Il localStorage lo conserva fra le sessioni.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(readOpenGroups);

  useEffect(() => {
    localStorage.setItem(OPEN_GROUPS_STORAGE_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  const setGroupOpen = useCallback((key: string, open: boolean) => {
    setOpenGroups((prev) => (prev[key] === open ? prev : { ...prev, [key]: open }));
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const setSidebarCollapsed = (collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
  };

  const setMobileSidebarOpen = (open: boolean) => {
    setIsMobileSidebarOpen(open);
  };

  return (
    <SidebarContext.Provider value={{
      isSidebarCollapsed,
      isMobileSidebarOpen,
      toggleSidebar,
      toggleMobileSidebar,
      setSidebarCollapsed,
      setMobileSidebarOpen,
      openGroups,
      setGroupOpen
    }}>
      {children}
    </SidebarContext.Provider>
  );
};
